import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Heart, Tag, ChevronRight, Clock } from "lucide-react";

const STATUS_LABEL = {
    pending: "Ελήφθη", accepted: "Δεκτή", preparing: "Ετοιμάζεται",
    ready: "Έτοιμη", on_the_way: "Στον δρόμο",
    completed: "Παραδόθηκε", delivered: "Παραδόθηκε",
    cancelled: "Ακυρώθηκε", rejected: "Απορρίφθηκε",
};
const STATUS_COLOR = {
    pending: "#f4b339", accepted: "#39ff8f", preparing: "#ff9f1c",
    ready: "#00f0ff", on_the_way: "#00f0ff",
    completed: "#39ff8f", delivered: "#39ff8f",
    cancelled: "#888", rejected: "#ff2d4f",
};

export const MyOrders = () => {
    const { user, loading } = useAuth();
    const { add, setOpen: openCart } = useCart();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [stores, setStores] = useState([]);
    const [tab, setTab] = useState("orders"); // orders | favorites
    const [favs, setFavs] = useState(() => JSON.parse(localStorage.getItem("favs") || "[]"));

    useEffect(() => {
        if (loading) return;
        if (!user && tab === "orders") return; // soft fallback
        if (user) api.get("/orders/me").then(({ data }) => setOrders(data || []));
        api.get("/stores").then(({ data }) => setStores(data || []));
    }, [user, loading, tab]);

    const reorder = async (order) => {
        // Add all items from past order to cart
        const menu = await api.get("/menu").then((r) => r.data);
        const menuMap = Object.fromEntries(menu.map((m) => [m.id, m]));
        let added = 0;
        order.items.forEach((it) => {
            const m = menuMap[it.item_id] || { id: it.item_id, name: it.name, price: it.price, image_url: "" };
            for (let i = 0; i < it.quantity; i++) {
                add({ id: m.id, name: m.name, price: m.price, image_url: m.image_url, category: m.category }, { silent: true });
                added++;
            }
        });
        toast.success(`Προστέθηκαν ${added} προϊόντα στο καλάθι!`);
        openCart(true);
    };

    const toggleFav = (storeId) => {
        const next = favs.includes(storeId) ? favs.filter((x) => x !== storeId) : [...favs, storeId];
        setFavs(next);
        localStorage.setItem("favs", JSON.stringify(next));
    };

    const favoriteStores = stores.filter((s) => favs.includes(s.id));

    return (
        <div className="cyber-bg min-h-screen text-white pb-20" data-testid="my-orders-page">
            <header className="sticky top-0 z-30 glass-gold border-b border-[var(--gold)]/20 px-4 py-3 flex items-center gap-3">
                <button onClick={() => navigate("/")} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10" data-testid="orders-back">
                    <ArrowLeft size={16} />
                </button>
                <h1 className="font-heading text-2xl flex-1" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Λογαριασμός</h1>
            </header>

            {/* Tabs */}
            <div className="px-4 py-3 flex gap-2 border-b border-white/5">
                <button data-testid="tab-orders" onClick={() => setTab("orders")} className={`pill-chip ${tab === "orders" ? "active" : ""}`}>
                    <Clock size={12} /> Παραγγελίες ({orders.length})
                </button>
                <button data-testid="tab-favorites" onClick={() => setTab("favorites")} className={`pill-chip ${tab === "favorites" ? "active" : ""}`}>
                    <Heart size={12} /> Αγαπημένα ({favoriteStores.length})
                </button>
            </div>

            <div className="px-4 py-4 max-w-3xl mx-auto">
                {tab === "orders" ? (
                    !user ? (
                        <div className="text-center py-20">
                            <p className="text-white/60 mb-4">Συνδέσου για να δεις τις παραγγελίες σου.</p>
                            <button onClick={() => navigate("/login")} className="gold-cta h-11 px-6 rounded-full font-mono uppercase tracking-widest text-xs font-bold">
                                Σύνδεση
                            </button>
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-white/40 font-mono text-xs uppercase tracking-widest mb-4">// no_orders_yet</p>
                            <Link to="/" className="gold-cta inline-flex h-11 px-6 rounded-full items-center gap-2 font-mono uppercase tracking-widest text-xs font-bold">
                                Ανακάλυψε εστιατόρια <ChevronRight size={14} />
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {orders.map((o) => (
                                <article key={o.id} className="glass-gold rounded-2xl p-4" data-testid={`order-row-${o.id}`}>
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <p className="font-mono text-[10px] text-white/40">#{o.id.slice(0, 8).toUpperCase()}</p>
                                            <p className="text-xs text-white/55">{new Date(o.created_at).toLocaleString("el-GR")}</p>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-widest font-bold border" style={{ color: STATUS_COLOR[o.status], borderColor: STATUS_COLOR[o.status] + "66", background: STATUS_COLOR[o.status] + "10" }}>
                                            {STATUS_LABEL[o.status] || o.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-white/75 mb-3 space-y-0.5">
                                        {o.items.slice(0, 3).map((i, idx) => (
                                            <p key={idx} className="truncate">
                                                <span className="text-[var(--gold-light)] font-bold">{i.quantity}×</span> {i.name}
                                            </p>
                                        ))}
                                        {o.items.length > 3 && <p className="text-white/40 text-xs">+ {o.items.length - 3} ακόμα</p>}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading text-xl gold-text">€{o.total.toFixed(2)}</span>
                                        <div className="flex gap-2">
                                            <Link to={`/order/${o.id}`} className="h-10 px-4 rounded-full border-2 border-white/15 hover:border-white/30 font-mono text-[11px] uppercase tracking-widest font-bold flex items-center transition">
                                                Λεπτομέρειες
                                            </Link>
                                            <button onClick={() => reorder(o)} data-testid={`reorder-${o.id}`} className="gold-cta h-10 px-4 rounded-full font-mono text-[11px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition">
                                                <RefreshCw size={12} strokeWidth={3} /> Ξανά
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )
                ) : (
                    favoriteStores.length === 0 ? (
                        <div className="text-center py-20">
                            <Heart size={32} className="mx-auto text-white/20 mb-3" />
                            <p className="text-white/40 font-mono text-xs uppercase tracking-widest mb-4">// no_favorites</p>
                            <p className="text-white/55 text-sm mb-4">Πάτα την καρδιά σε ένα store για να το αποθηκεύσεις.</p>
                            <button onClick={() => navigate("/")} className="gold-cta h-11 px-6 rounded-full font-mono uppercase tracking-widest text-xs font-bold">
                                Εξερεύνησε
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {favoriteStores.map((s) => (
                                <article key={s.id} data-testid={`fav-store-${s.id}`} className="glass-gold rounded-2xl overflow-hidden flex">
                                    <img src={s.image_url || s.logo_url} alt={s.name} className="w-24 h-28 object-cover" />
                                    <div className="flex-1 p-3 min-w-0 relative">
                                        <button onClick={() => toggleFav(s.id)} className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full hover:bg-white/10">
                                            <Heart size={13} fill="var(--gold-light)" className="text-[var(--gold-light)]" />
                                        </button>
                                        <h3 className="font-heading text-base truncate" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{s.name}</h3>
                                        <p className="text-[10px] text-white/50 capitalize truncate">{s.cuisine} · {s.delivery_minutes}'</p>
                                        <p className="text-[10px] text-[var(--gold-light)] mt-1">★ {s.rating.toFixed(1)} ({s.rating_count})</p>
                                        <Link to={`/store/${s.slug}`} className="absolute bottom-2 right-2 text-[10px] font-mono uppercase tracking-widest text-[var(--gold-light)] hover:underline">
                                            Άνοιγμα →
                                        </Link>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};
